import { Alert, ButtonLink } from '../../design/index.js';

/**
 * The close (§27).
 *
 * The only dark band on the page, the way a salon's lights go down at closing:
 * one sentence, two ways forward. A `mailto:` rather than a
 * form, because this deployment has no endpoint behind a form and a submit
 * button that silently does nothing is worse than an address a person reads.
 * When a contact endpoint exists, this is the one place to change.
 *
 * ## The empty-address case is not decoration
 *
 * `LEGAL_NOTICE.contactEmail` starts empty, because inventing a company's
 * address is exactly what `legal-notice.ts` refuses to do. So when it is
 * empty, this renders a visible notice instead of two buttons that go
 * nowhere. A dead primary call to action on a landing page is the most
 * expensive silent failure a product can ship, and this makes it loud for
 * whoever deploys it rather than quiet for everyone who visits.
 */
export function FinalCta({ contactEmail }: { readonly contactEmail: string }): React.JSX.Element {
  return (
    <section id="demo" className="on-dark bg-violet py-section">
      <div className="shell">
        <h2 className="sign max-w-4xl text-balance text-sign text-ink-light">
          Votre prochain appel peut déjà être pris.
        </h2>

        <p className="mt-5 max-w-xl text-lead text-ink-light-muted">
          Dites-nous quel logiciel de réservation vous utilisez et à quelle heure vous appeler. Nous
          vous montrons l’assistante sur votre propre catalogue.
        </p>

        {contactEmail === '' ? (
          <div className="mt-8 max-w-xl">
            <Alert kind="warning" title="Aucune adresse de contact n’est configurée">
              Renseignez <code>contactEmail</code> dans <code>lib/legal-notice.ts</code> avant la
              mise en ligne. Sans elle, cette page n’a aucun moyen d’être recontactée — et c’est la
              seule chose qu’elle doit savoir faire.
            </Alert>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <ButtonLink
              href={`mailto:${contactEmail}?subject=${encodeURIComponent('Démonstration GSI Voice')}`}
              tone="dark"
              variant="primary"
              size="lg"
            >
              Demander une démonstration
            </ButtonLink>
            <ButtonLink href={`mailto:${contactEmail}`} tone="dark" variant="secondary" size="lg">
              Écrire à GSI Consulting
            </ButtonLink>
          </div>
        )}

        <p className="mt-6 text-ui-sm text-ink-light-muted">
          GSI Voice est une solution GSI Consulting.
        </p>
      </div>
    </section>
  );
}
